/**
 * PipelineActivityLogDrawerView — pure presentation for the per-pipeline
 * activity log (SE-5504 AC3, filters SE-6251, restyled SE-6554).
 *
 * Right-anchored side drawer on desktop, bottom sheet on small screens — one
 * Drawer atom with `anchor` switched by SCREEN SIZE, never device type
 * (`.claude/rules/screen-size-not-device-type.md`).
 *
 * Shows ALL pipeline audit events, not only card movements (Pietra, resolved
 * 2026-07-16): every entry `pipelineActivityLog` returns for the board is
 * rendered, newest-first.
 *
 * ## The filter chips (SE-6251)
 *
 * The chip rows sit directly above the trail. They are REAL server-side
 * filters: a press calls a pre-built handler, the container folds the selection
 * into the query's `action` / `actorType` variables, and the server returns a
 * smaller page. This View receives `rows` already filtered by the backend and
 * renders every one of them — it has no access to the enum payload behind a
 * chip and therefore cannot narrow anything, which is the structural version of
 * SE-5504's "surface the backend, never mask it" rule.
 *
 * The chips stay in TWO LABELLED ROWS rather than the single flat row the
 * restyle spec draws, and that is a correctness constraint, not a stylistic
 * one. The two rows are two independent query arguments combined with AND: a
 * flat row mixing them would read as one OR-list, so selecting "Moves" beside
 * "Automated" would promise "moves or automated entries" while the server
 * returns only automated moves. The pill treatment, spacing and the leading
 * "All" chip all follow the spec; only the grouping the data actually has is
 * preserved.
 *
 * ## The timeline (SE-6554)
 *
 * Entries are grouped under MONTH headers and drawn as an actor-avatar
 * timeline, which is why this surface no longer renders the shared
 * `PipelineActivityTrail`. That molecule is still the card-scoped trail inside
 * the player preview drawer, and the two surfaces have diverged: the manager
 * log wants month headers, a relative stamp and the mover's initials, the
 * preview drawer wants the compact per-card list. Rendering the timeline here
 * — rather than adding modes to the shared molecule — is what keeps this
 * restyle from moving the preview drawer's pixels. Both still speak the same
 * `TEST_ID_PIPELINE_ACTIVITY` anchor vocabulary, so a spec asserts the same
 * ids on either surface.
 *
 * Automated entries render a bolt and NO avatar. That pairing is deliberate:
 * initials are how this surface says "a person did this", so a rule-generated
 * entry must never borrow a disc, and the row model's `isAutomated` — not a
 * name heuristic — is what decides.
 *
 * `react-perf` forbids creating closures inside a `map`, which is exactly a
 * chip row's shape, so press handlers arrive pre-built and keyed by chip —
 * the `filterHandlers` pattern `ReviewQueueDrawerView` established.
 * @remarks View component — no business logic, hooks, or state.
 */

import { memo } from "react";
import { XIcon } from "lucide-react-native";

import {
  TEST_ID_PIPELINE_ACTIVITY,
  TEST_ID_PIPELINE_ACTIVITY_LOG,
} from "e2e/testId";

import { Avatar, AvatarFallbackText } from "@/components/atoms/Avatar";
import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from "@/components/atoms/Drawer";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import {
  PIPELINE_DRAWER_CONTENT_CLASS,
  PIPELINE_DRAWER_SHEET_CLASS,
} from "../../../constants";
import {
  describeActivityHeadline,
  formatActivityRelativeTimestamp,
  groupActivityRowsByMonth,
} from "../../../utils/pipelineActivityTimeline";

import type { PipelineActivityChipLabel } from "../../../utils/pipelineActivityFilters";
import type { PipelineActivityRow } from "../../../utils/pipelineActivityRows";
import type { PipelineActivityMonthGroup } from "../../../utils/pipelineActivityTimeline";

/** One labelled chip row — a filter group the user multi-selects within. */
export interface PipelineActivityFilterGroupProps {
  /** Stable group key (also the row's react key). */
  readonly key: string;
  /** Row heading, e.g. "Event" or "Actor". */
  readonly label: string;
  /** Chips in this row. */
  readonly chips: readonly PipelineActivityChipLabel[];
  /** Keys of the chips currently selected in this row. */
  readonly selectedKeys: ReadonlySet<string>;
}

/** Props for {@link PipelineActivityLogDrawerView}. */
export interface PipelineActivityLogDrawerViewProps {
  /** Whether the drawer is open. */
  readonly isOpen: boolean;
  /** Close handler. */
  readonly onClose: () => void;
  /** Whether the small-screen (bottom-sheet) layout is active. */
  readonly isSmallScreen: boolean;
  /** Filter chip rows, top to bottom. */
  readonly filterGroups: readonly PipelineActivityFilterGroupProps[];
  /**
   * Pre-built per-chip press handlers keyed by chip key. Pre-built because
   * `react-perf` forbids creating a closure inside the `map` below.
   */
  readonly chipHandlers: Readonly<Record<string, () => void>>;
  /** Every entry the server returned, newest-first. */
  readonly rows: readonly PipelineActivityRow[];
  /** Server-reported total for the pipeline. */
  readonly totalCount: number;
  /** Whether the first page is loading. */
  readonly loading: boolean;
  /** Whether the read failed. */
  readonly isError: boolean;
  /** Whether more pages exist. */
  readonly hasMore: boolean;
  /** Whether a "Load more" page is in flight. */
  readonly isLoadingMore: boolean;
  /** Fetches the next page. */
  readonly onLoadMore: () => void;
}

/**
 * Frozen accessibility states for a chip.
 *
 * Hoisted rather than written inline because `react-perf` forbids passing a
 * freshly-created object as a JSX prop — an inline `{{ checked }}` would be a
 * new identity on every render.
 *
 * `checkbox` + `checked` (rather than `button` + `selected`) is the pairing
 * that survives the round trip: a chip row is multi-select, and on web React
 * Native maps it to `role="checkbox"` / `aria-checked`, which a Playwright
 * `getByRole("checkbox", { checked })` query can read. `aria-selected` on a
 * button is not a valid ARIA pairing and is not exposed.
 */
const CHIP_STATE = {
  checked: { checked: true },
  unchecked: { checked: false },
} as const;

/**
 * Render one labelled chip row.
 *
 * Module-scope (not inline) because the View is an arrow-shorthand component
 * with no statement body, the same reason `PipelineActivityTrailView` hoists
 * `renderState`. `onPress` reads a PRE-BUILT handler out of the map rather than
 * closing over the chip, which is what keeps `react-perf`'s no-closure-in-map
 * rule satisfied.
 *
 * The pill's chrome — radius, fill, border, label colour — is the Badge atom's
 * closed tone pairing; the Pressable adds only the hit area and the
 * accessibility identity, which must sit on the SAME node as the `testID` for
 * a state assertion to find it.
 * @param group - The chip row to render.
 * @param chipHandlers - Pre-built per-chip press handlers, keyed by chip key.
 * @returns The chip row.
 */
const renderFilterGroup = (
  group: PipelineActivityFilterGroupProps,
  chipHandlers: Readonly<Record<string, () => void>>
) => (
  <Stack key={group.key} className="gap-2">
    <Text variant="micro-strong" className="tracking-wide text-content-muted">
      {group.label}
    </Text>
    <Stack direction="horizontal" className="flex-wrap items-center gap-2">
      {group.chips.map(chip => (
        <Pressable
          key={chip.key}
          onPress={chipHandlers[chip.key]}
          accessibilityLabel={`Filter by ${chip.label}`}
          accessibilityRole="checkbox"
          accessibilityState={
            group.selectedKeys.has(chip.key)
              ? CHIP_STATE.checked
              : CHIP_STATE.unchecked
          }
          testID={TEST_ID_PIPELINE_ACTIVITY_LOG.chipFor(chip.key)}
        >
          <TonePill
            tone={
              group.selectedKeys.has(chip.key)
                ? "info-tinted"
                : "neutral-raised"
            }
            label={chip.label}
            textVariant="body-strong"
            className="h-9 px-4"
          />
        </Pressable>
      ))}
    </Stack>
  </Stack>
);

/**
 * Render one timeline entry.
 *
 * The avatar is present for human entries and absent for automated ones; the
 * bolt is its own node rather than text spliced into the headline so the
 * automation marker keeps a stable anchor. The quote line appears only when the
 * mover actually typed a reason into the sensitive-stage prompt.
 * @param row - The entry.
 * @returns The entry node.
 */
const renderEntry = (row: PipelineActivityRow) => (
  <Stack
    key={row.id}
    direction="horizontal"
    className="items-start gap-2 pb-3"
    testID={TEST_ID_PIPELINE_ACTIVITY.rowFor(row.id)}
  >
    {row.isAutomated ? null : (
      <Avatar size="xs" className="h-7 w-7 shrink-0 bg-surface-inverse">
        <AvatarFallbackText className="font-heading text-content-inverse">
          {row.actorLabel}
        </AvatarFallbackText>
      </Avatar>
    )}
    <Stack className="min-w-0 flex-1 gap-1">
      {/* The bolt is a SIBLING of the headline, not a Text nested inside it:
          the underlying text primitive defaults to its own `md` size, so a
          nested node would silently render a step larger than the line it
          sits in rather than inheriting from it. */}
      <Stack direction="horizontal" className="items-start gap-1">
        {row.isAutomated ? (
          <Text
            variant="caption"
            className="shrink-0 text-content-primary"
            testID={TEST_ID_PIPELINE_ACTIVITY.autoMarkerFor(row.id)}
          >
            ⚡
          </Text>
        ) : null}
        <Text
          variant="caption"
          className="min-w-0 flex-1 text-content-primary"
          testID={TEST_ID_PIPELINE_ACTIVITY.actorFor(row.id)}
        >
          {describeActivityHeadline(row)}
        </Text>
      </Stack>
      {row.rationale === null ? null : (
        <Text variant="micro" className="text-content-secondary">
          {`“${row.rationale}”`}
        </Text>
      )}
      <Text
        variant="micro"
        className="text-content-muted"
        testID={TEST_ID_PIPELINE_ACTIVITY.timestampFor(row.id)}
      >
        {formatActivityRelativeTimestamp(row.occurredAt)}
      </Text>
    </Stack>
  </Stack>
);

/**
 * Render one month bucket: its header and every entry beneath it.
 * @param group - The month group.
 * @returns The month node.
 */
const renderMonth = (group: PipelineActivityMonthGroup) => (
  <Stack key={group.key} className="gap-2 pb-2">
    <Text variant="micro-strong" className="tracking-widest text-content-muted">
      {group.label}
    </Text>
    {group.rows.map(renderEntry)}
  </Stack>
);

/**
 * Render the spinner / error / empty state, or `null` when there are entries to
 * show. Module-scope because the View itself must be an arrow-shorthand
 * component with no statement body (`component-structure/no-return-in-view`).
 * @param props - The full view props.
 * @returns The state node, or `null` when the timeline should render.
 */
const renderState = (props: PipelineActivityLogDrawerViewProps) => {
  if (props.loading && props.rows.length === 0) {
    return (
      <Box
        testID={TEST_ID_PIPELINE_ACTIVITY.LOADING}
        className="items-center justify-center py-10"
      >
        <Spinner />
      </Box>
    );
  }
  if (props.isError) {
    return (
      <Box
        testID={TEST_ID_PIPELINE_ACTIVITY.ERROR}
        className="items-center justify-center py-10"
      >
        <Text variant="body" className="text-status-error">
          Could not load the activity log. Please try again.
        </Text>
      </Box>
    );
  }
  if (props.rows.length === 0) {
    return (
      <Box
        testID={TEST_ID_PIPELINE_ACTIVITY.EMPTY}
        className="items-center justify-center py-10"
      >
        <Text variant="body" className="text-content-secondary">
          No pipeline activity yet.
        </Text>
      </Box>
    );
  }
  return null;
};

/**
 * Render the month-grouped timeline. Reached only once {@link renderState} has
 * nothing to show, i.e. there is at least one entry.
 * @param props - The full view props.
 * @returns The timeline.
 */
const renderTimeline = (props: PipelineActivityLogDrawerViewProps) => (
  <Stack>
    {groupActivityRowsByMonth(props.rows).map(renderMonth)}
    {props.hasMore ? (
      <Button
        testID={TEST_ID_PIPELINE_ACTIVITY.LOAD_MORE}
        label="Load more"
        accessibilityLabel="Load more activity"
        variant="outline"
        tone="secondary"
        size="sm"
        isDisabled={props.isLoadingMore}
        isLoading={props.isLoadingMore}
        onPress={props.onLoadMore}
      />
    ) : null}
  </Stack>
);

/**
 * Render the pipeline activity log drawer.
 * @param props - {@link PipelineActivityLogDrawerViewProps}.
 * @returns The drawer.
 */
const PipelineActivityLogDrawerView = (
  props: PipelineActivityLogDrawerViewProps
) => (
  <Drawer
    isOpen={props.isOpen}
    onClose={props.onClose}
    anchor={props.isSmallScreen ? "bottom" : "right"}
    size={props.isSmallScreen ? "lg" : "md"}
  >
    <DrawerBackdrop />
    <DrawerContent
      testID={TEST_ID_PIPELINE_ACTIVITY_LOG.DRAWER}
      className={
        props.isSmallScreen
          ? PIPELINE_DRAWER_SHEET_CLASS
          : PIPELINE_DRAWER_CONTENT_CLASS
      }
    >
      <DrawerHeader className="border-b border-outline-default px-5 py-4">
        <Stack
          direction="horizontal"
          className="flex-1 items-center justify-between gap-4"
        >
          <Stack direction="horizontal" className="min-w-0 items-center gap-2">
            <Text variant="title-sm" className="text-content-primary">
              Activity
            </Text>
            {/* The server's FILTERED total, never a recount of `rows`: under a
                filter the page is capped, so recounting would turn a 40-of-312
                page into "40 events". */}
            <TonePill
              tone="neutral-raised"
              label={
                props.totalCount === 1
                  ? "1 event"
                  : `${props.totalCount} events`
              }
              textVariant="micro"
              className="shrink-0 px-2 py-micro"
              testID={TEST_ID_PIPELINE_ACTIVITY_LOG.TOTAL}
            />
          </Stack>
          <Pressable
            onPress={props.onClose}
            accessibilityLabel="Close pipeline activity log"
            testID={TEST_ID_PIPELINE_ACTIVITY_LOG.CLOSE}
            className="size-8 shrink-0 items-center justify-center rounded-md bg-surface-raised"
          >
            <Icon as={XIcon} size="sm" className="text-content-secondary" />
          </Pressable>
        </Stack>
      </DrawerHeader>

      <DrawerBody className="flex-1 p-0">
        {/* SE-6251 — server-side filter chips. The timeline below renders
            whatever the (already filtered) response contained. */}
        <Stack
          className="gap-3 px-5 pb-2 pt-3"
          testID={TEST_ID_PIPELINE_ACTIVITY_LOG.FILTERS}
        >
          {props.filterGroups.map(group =>
            renderFilterGroup(group, props.chipHandlers)
          )}
        </Stack>
        <ScrollView showsVerticalScrollIndicator={false}>
          <Stack className="px-5 pb-5 pt-2">
            {renderState(props) ?? renderTimeline(props)}
          </Stack>
        </ScrollView>
      </DrawerBody>
    </DrawerContent>
  </Drawer>
);

PipelineActivityLogDrawerView.displayName = "PipelineActivityLogDrawerView";

export default memo(PipelineActivityLogDrawerView);
